import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { updateProfile } from '../../store/reducers/userSlice';
import {
  setIsPaymentPopupOpen,
  setIsPaymentPopupFromBrandLimit,
} from '../../store/reducers/uiSlice';
import { fetchAppInit } from '../../lib/api/app-init';
import { HOME_ROUTE } from '../../lib/constants/route';
import ClientCard from './ClientCard';
import AddClientForm from './AddClientForm';
import RenameBrandForm from './RenameBrandForm';
import DeleteBrandForm from './DeleteBrandForm';
import Popup from '../../ui/popup/Popup';
import BrandLimitUpgradePopup from '../../components/sidebar/BrandLimitUpgradePopup';
import { request_package_subscription } from '../../lib/api/ai-tracker/subscriptions/make_package_subscriptions_api_call';
import { getCheckoutFailureMessage } from '../../lib/api/ai-tracker/subscriptions/subscription-error-message';
import { createShop } from '../../lib/api/shop/create_shop';
import { setMessage } from '../../store/reducers/appSlice';
import { plusSignSVG, shopSVG } from '../../lib/allCustomSVGs';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';
import {
  RoleGate,
  filterByAccessibleShops,
  useAccessibleShops,
  useCan,
} from '../../lib/rbac';
import '../../styles/pages/clients/clients.scss';

const ClientsList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userProfile = useSelector((state: RootState) => state.user.profile);
  const taraSubscriptionDetails: any = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );
  const taraTrialDetails = useSelector(
    (state: RootState) => state.user?.shop?.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };

  const isActiveTrialWithDays =
    !!taraTrialDetails?.is_trial &&
    taraTrialDetails?.trial_available_days != null &&
    taraTrialDetails.trial_available_days > 0;

  // Phase 12 — RBAC filter. Owners (null) see all shops; non-owners see only
  // the shops their employee identity grants them via accessible_shops.
  const accessibleShops = useAccessibleShops();
  const employeeIdentity = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity
  );
  // SECURITY: scope the brand list to the shops /api/v1/me grants this employee
  // — including OWNERS (MeController returns all of the owner's org shops). Do
  // NOT fall back to unfiltered `user.profile.all_shops`: it comes from the
  // seller-init/seller-JWT surface, is NOT scoped to this employee's org, and
  // persists across logins — surfacing it leaked another account's brands into
  // this list (and the empty intersection produced the "no brands" symptom).
  const scopedSlugs = useMemo<string[]>(() => {
    const fromShops = (employeeIdentity?.accessible_shops ?? [])
      .map((s: any) => s.slug ?? s.brand_slug)
      .filter((v: any): v is string => Boolean(v));
    if (fromShops.length > 0) return fromShops;
    return (employeeIdentity?.accessible_brands ?? [])
      .map((b: any) => b.brand_slug)
      .filter((v: any): v is string => Boolean(v));
  }, [employeeIdentity]);
  const allShopsRaw = userProfile?.all_shops;
  const allShops = useMemo(
    () => filterByAccessibleShops(allShopsRaw ?? [], scopedSlugs),
    [allShopsRaw, scopedSlugs]
  );
  const activeShopSlug = userProfile?.active_shop_slug;
  const isNonOwner = accessibleShops !== null;
  // Who may rename a brand: owner/manager (clients.manage umbrella write cap).
  const canManageBrands = useCan('clients.manage');
  // Deleting is stricter than renaming — owner only (`clients.delete`).
  const canDeleteBrands = useCan('clients.delete');

  const [searchQuery, setSearchQuery] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingShopSlug, setSwitchingShopSlug] = useState<string | null>(
    null
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [renamingShop, setRenamingShop] = useState<{
    slug: string;
    name: string;
  } | null>(null);
  const [deletingShop, setDeletingShop] = useState<{
    slug: string;
    name: string;
  } | null>(null);
  const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const filteredShops = useMemo(() => {
    if (!allShops) return [];
    if (!searchQuery.trim()) {
      const active = allShops.find((shop) => shop.slug === activeShopSlug);
      const others = allShops.filter((shop) => shop.slug !== activeShopSlug);
      return active ? [active, ...others] : allShops;
    }
    return allShops.filter((shop) =>
      shop.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allShops, searchQuery, activeShopSlug]);

  const handleShopSwitch = (shopSlug: string) => {
    if (isSwitching || shopSlug === activeShopSlug) return;

    setIsSwitching(true);
    setSwitchingShopSlug(shopSlug);

    setTimeout(() => {
      try {
        dispatch(updateProfile({ active_shop_slug: shopSlug }));
        navigate(HOME_ROUTE);
        fetchAppInit(true);
      } catch (error) {
        console.error('Error switching shop:', error);
      }
    }, 2000);
  };

  // Drop the deleted brand from the cached profile so the grid updates without
  // a full re-init. If it was the active brand we must also move the session to
  // a surviving one — everything downstream keys off `active_shop_slug`.
  const handleBrandDeleted = (deletedSlug: string) => {
    const remaining = (allShopsRaw ?? []).filter(
      (shop) => shop.slug !== deletedSlug
    );
    const wasActive = deletedSlug === activeShopSlug;
    const nextActiveSlug = wasActive
      ? remaining.find((shop) => scopedSlugs.includes(shop.slug))?.slug ??
        remaining[0]?.slug
      : activeShopSlug;

    dispatch(
      updateProfile({
        all_shops: remaining,
        ...(wasActive && nextActiveSlug
          ? { active_shop_slug: nextActiveSlug }
          : {}),
      })
    );

    if (wasActive) {
      fetchAppInit(true);
    }
  };

  const handleAddNewClick = () => {
    const isAgency = userProfile?.role === 'agency';
    const seatCount = parseInt(
      taraSubscriptionDetails?.purchased_shops?.seat_count || '0'
    );
    const shopsCount = allShops?.length || 0;

    if (isAgency && shopsCount >= seatCount && !isActiveTrialWithDays) {
      setIsUpgradePopupOpen(true);
    } else {
      setShowAddForm(true);
    }
  };

  const handleBrandLimitReached = () => {
    setShowAddForm(false);
    dispatch(setIsPaymentPopupFromBrandLimit(true));
    setTimeout(() => {
      dispatch(setIsPaymentPopupOpen(true));
    }, 300);
  };

  const handleConfirmUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const currentPackage = taraSubscriptionDetails?.package_slug;
      const currentSeatCount = parseInt(
        taraSubscriptionDetails?.purchased_shops?.seat_count || '0'
      );

      if (!currentPackage) {
        throw new Error('No active package found');
      }

      const upgradeResult = await request_package_subscription(
        currentPackage,
        currentSeatCount + 5
      );
      const upgradeRes = upgradeResult.ok ? upgradeResult.data : false;

      if (upgradeRes) {
        if (
          upgradeRes?.payment_details?.payment_details?._links?.checkout?.href
        ) {
          window.location.href =
            upgradeRes.payment_details.payment_details._links.checkout.href;
          return;
        }

        const tempName = `${userProfile?.email}'s shop`;
        const createRes = await createShop(tempName);

        if (createRes.success && createRes.data) {
          dispatch(updateProfile({ active_shop_slug: createRes.data.slug }));
          setIsUpgradePopupOpen(false);
          navigate(HOME_ROUTE);
          fetchAppInit(true);
        } else {
          dispatch(
            setMessage({
              message:
                createRes.message || 'Brand creation failed after upgrade',
              messageType: 'error',
            })
          );
        }
      } else {
        dispatch(
          setMessage({
            message: getCheckoutFailureMessage(
              upgradeResult.ok ? undefined : upgradeResult.message,
              t('Checkout-StartFailed')
            ),
            messageType: 'error',
          })
        );
      }
    } catch (error: any) {
      console.error('Upgrade flow error:', error);
      dispatch(
        setMessage({
          message: error.message || 'An error occurred during upgrade',
          messageType: 'error',
        })
      );
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <div className='clientsList'>
      <div className='clientsList__header'>
        <div className='clientsList__header-left'>
          <h1 className='clientsList__title'>Brands</h1>
          <span className='clientsList__count'>
            {allShops?.length || 0}{' '}
            {allShops?.length === 1 ? 'brand' : 'brands'}
          </span>
        </div>
        <RoleGate capability='clients.create'>
          <button
            type='button'
            className='clientsList__add-btn'
            onClick={handleAddNewClick}
            disabled={isSwitching}
          >
            <CustomSVGs svg={plusSignSVG} />
            <span>Add Brand</span>
          </button>
        </RoleGate>
      </div>

      {showAddForm && (
        <RoleGate capability='clients.create'>
          <AddClientForm
            onClose={() => setShowAddForm(false)}
            onBrandLimitReached={handleBrandLimitReached}
          />
        </RoleGate>
      )}

      <div className='clientsList__search'>
        <input
          type='text'
          placeholder='Search brands...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='clientsList__search-input'
          disabled={isSwitching}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className='clientsList__search-clear'
            type='button'
          >
            x
          </button>
        )}
      </div>

      {filteredShops.length === 0 ? (
        searchQuery ? (
          <div className='clientsList__empty'>
            {`No clients matching "${searchQuery}"`}
          </div>
        ) : isNonOwner ? (
          <div className='clientsList__empty'>
            No clients assigned to you yet. Ask your team lead to grant access.
          </div>
        ) : (
          <TeachingEmptyState
            icon={shopSVG}
            title={t('ClientsEmptyTitle')}
            body={t('ClientsEmptyBody')}
            actionLabel={t('ClientsEmptyAction')}
            onAction={handleAddNewClick}
          />
        )
      ) : (
        <div className='clientsList__grid'>
          {filteredShops.map((shop) => (
            <ClientCard
              key={shop.id}
              shop={shop}
              isActive={shop.slug === activeShopSlug}
              isSwitching={isSwitching}
              isSwitchingTarget={isSwitching && switchingShopSlug === shop.slug}
              canManage={canManageBrands}
              canDelete={canDeleteBrands}
              onClick={() => handleShopSwitch(shop.slug)}
              onRename={() =>
                setRenamingShop({ slug: shop.slug, name: shop.name })
              }
              onDelete={() =>
                setDeletingShop({ slug: shop.slug, name: shop.name })
              }
            />
          ))}
        </div>
      )}

      {renamingShop && (
        <RoleGate capability='clients.manage'>
          <Popup
            trigger={!!renamingShop}
            setTrigger={() => setRenamingShop(null)}
          >
            <RenameBrandForm
              shopSlug={renamingShop.slug}
              currentName={renamingShop.name}
              onClose={() => setRenamingShop(null)}
            />
          </Popup>
        </RoleGate>
      )}

      {deletingShop && (
        <RoleGate capability='clients.delete'>
          <Popup
            trigger={!!deletingShop}
            setTrigger={() => setDeletingShop(null)}
          >
            <DeleteBrandForm
              shopSlug={deletingShop.slug}
              shopName={deletingShop.name}
              isActive={deletingShop.slug === activeShopSlug}
              onClose={() => setDeletingShop(null)}
              onDeleted={handleBrandDeleted}
            />
          </Popup>
        </RoleGate>
      )}

      <BrandLimitUpgradePopup
        isOpen={isUpgradePopupOpen}
        onClose={() => setIsUpgradePopupOpen(false)}
        onConfirm={handleConfirmUpgrade}
        isLoading={isUpgrading}
      />
    </div>
  );
};

export default ClientsList;
